// src/pages/.../Estoque-Generico/components/ProdutoDetailSidePanel.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../../context/auth-context";

export type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

type CurrencyCode = "BRL" | "USD" | "EUR";

type ProdutoAnyAPI = {
  id?: number | string | null;
  produto_id?: number | string | null;

  codigo?: string | null;
  ncm?: string | null;
  nome?: string | null;
  classificacao?: string | null;
  posicao?: string | null;
  vendavel?: boolean | null;
  responsavel_registro?: number | string | null;
  departamento_legado?: string | null;
  departamento?: string | null;
  peso?: string | number | null;
  dimensao?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
  ocultar?: boolean | null;
  empresa_id?: number | string | null;

  estoque_id?: number | string | null;
  estoque_codigo?: string | null;
  estoque_nome?: string | null;

  modulo_id?: number | string | null;
  modulo_codigo?: string | null;
  modulo_label?: string | null;

  quantidade_atual?: number | string | null;
  custo_unitario?: string | number | null;
  custo_unitario_atual?: string | number | null;
  ultimo_custo_unitario?: string | number | null;
  moeda?: string | null;
  ultima_moeda?: string | null;
};

type ApiMovItem = {
  id?: number | string | null;
  item_id?: number | string | null;
  movimentacao_id?: number | string | null;
  produto_id?: number | string | null;
  estoque_id?: number | string | null;

  tipo?: string | null;
  tipo_full?: string | null;
  codigo_movimentacao?: string | null;
  tipo_codigo?: string | null;
  tipo_descricao?: string | null;

  quantidade?: number | string | null;
  custo_unitario?: number | string | null;
  moeda?: string | null;

  data_movimentacao?: string | null;
  criado_em?: string | null;

  responsavel_id?: number | string | null;
  responsavel_nome?: string | null;

  motivo?: string | null;
  os_numero?: string | null;
  proposta_venda?: string | null;
  origem_mov_id?: number | string | null;

  quantidade_anterior?: number | string | null;
  quantidade_nova?: number | string | null;
  quantidade_definida?: number | string | null;
  quantidade_atual?: number | string | null;
  saldo_anterior?: number | string | null;
  saldo_atual?: number | string | null;
  delta?: number | string | null;

  metadados_origem?: Record<string, unknown> | string | null;
  metadados?: Record<string, unknown> | string | null;
  metadata?: Record<string, unknown> | string | null;

  [key: string]: unknown;
};

type MovimentosPorTipo = {
  entrada?: ApiMovItem[];
  saida?: ApiMovItem[];
  perda?: ApiMovItem[];
  venda?: ApiMovItem[];
  consumo?: ApiMovItem[];
  retorno?: ApiMovItem[];
  standby?: ApiMovItem[];
  ajuste?: ApiMovItem[];
  [key: string]: ApiMovItem[] | undefined;
};

type ProdutoDetalhePayload = {
  produto: ProdutoAnyAPI;
  custos: ApiMovItem[];
  movimentos_por_tipo?: MovimentosPorTipo;
  estoque_atual: {
    produto_id?: number | string | null;
    estoque_id?: number | string | null;
    quantidade_atual?: number | string | null;
    custo_unitario_atual?: string | number | null;
    moeda?: string | null;
  } | null;
};

type GetProdutoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  fonte?: "cache" | "banco";
  versao?: number;
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    empresa_id?: number | string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
    icone?: string | null;
    path?: string | null;
  };
  data?: ProdutoAnyAPI[] | ProdutoDetalhePayload | null;
  visiveis?: ProdutoAnyAPI[] | null;
  ocultos?: ProdutoAnyAPI[] | null;
  oculto?: boolean;
};

type PostEditarProdutoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  trace_id?: string;
  data?: {
    produto?: ProdutoAnyAPI;
  };
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    empresa_id?: number | string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
    icone?: string | null;
    path?: string | null;
  };
};

type PostCorrigirMovimentacaoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  trace_id?: string;
  data?: {
    movimentacao_ajuste_id?: number | string | null;
    movimentacao_origem_id?: number | string | null;
    movimentacao_item_origem_id?: number | string | null;
  };
};

type CorrectionFormState = {
  moeda: CurrencyCode;
  custoUnitario: string;
  motivo: string;
};

type EditFormState = {
  codigo: string;
  nome: string;
  ncm: string;
  classificacao: string;
  posicao: string;
  vendavel: boolean;
  peso: string;
  dimensao: string;
  quantidadeAtual: string;
};

type HistoryTone =
  | "entrada"
  | "saida"
  | "retorno"
  | "ajuste"
  | "perda"
  | "preco"
  | "neutro";

type MovimentosResumoBucket = {
  movimentacoes: number;
  quantidadeTotal: number;
};

type MovimentosResumo = {
  entradas: MovimentosResumoBucket;
  saidas: MovimentosResumoBucket;
  retornos: MovimentosResumoBucket;
  ajustes: MovimentosResumoBucket;
  perdas: MovimentosResumoBucket;
  outros: MovimentosResumoBucket;
};

const API_LOGVI_FALLBACK_BASE = "https://api.logvi-havox.com";
const ACCESS_TOKEN_KEYS = ["accessToken", "eva:accessToken", "access_token"];

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const PRODUTO_DETAIL_SIDE_PANEL_CSS = `
.produto-detail-backdrop {
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  left: ${LEFTBAR_WIDTH}px;
  right: 0;
  bottom: 0;
  z-index: 1190;
  display: flex;
  justify-content: flex-end;
  background:
    radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%),
    rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  box-sizing: border-box;
}

.produto-detail-panel {
  width: min(1020px, calc(100vw - ${LEFTBAR_WIDTH}px));
  height: calc(100vh - ${HEADER_HEIGHT}px);
  background: #ffffff;
  border-left: 1px solid #d9e2ec;
  box-shadow: -18px 0 46px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  color: #0f172a;
}

.produto-detail-header {
  padding: 18px 20px 16px;
  border-bottom: 1px solid #e5eaf0;
  background:
    linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(255, 255, 255, 1)),
    #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.produto-detail-header-text {
  min-width: 0;
}

.produto-detail-eyebrow {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.42px;
  text-transform: uppercase;
}

.produto-detail-title {
  margin: 6px 0 4px;
  color: #0f172a;
  font-size: 21px;
  font-weight: 950;
  line-height: 1.18;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
}

.produto-detail-subtitle {
  margin: 0;
  max-width: 780px;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 760;
  line-height: 1.45;
}

.produto-detail-header-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.produto-detail-action-button {
  height: 36px;
  border: 1px solid #2f3f59;
  background: #2f3f59;
  color: #ffffff;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.16s ease,
    opacity 0.16s ease;
}

.produto-detail-action-button:hover:not(:disabled) {
  background: #243247;
  border-color: #243247;
  transform: translateY(-1px);
}

.produto-detail-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.produto-detail-close-button {
  width: 36px;
  height: 36px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.produto-detail-close-button:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateY(-1px);
}

.produto-detail-content {
  flex: 1;
  overflow-y: auto;
  background:
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.produto-detail-notice,
.produto-detail-empty,
.produto-detail-more-items {
  border: 1px solid #dbe4ef;
  background: #ffffff;
  padding: 14px;
  color: #475569;
  font-size: 12.5px;
  font-weight: 850;
}

.produto-detail-more-items {
  text-align: center;
}

.produto-detail-empty {
  border-style: dashed;
  color: #64748b;
}

.produto-detail-error {
  border: 1px solid #fecaca;
  background: linear-gradient(180deg, #fffafa, #fff7f7);
  color: #991b1b;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
}

.produto-detail-error strong {
  font-size: 14px;
  font-weight: 950;
}

.produto-detail-retry-button {
  width: fit-content;
  height: 32px;
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #991b1b;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
}

.produto-detail-retry-button:hover {
  background: #fef2f2;
}

.produto-detail-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.produto-detail-stat-card {
  border: 1px solid #e2e8f0;
  background:
    linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  padding: 13px 13px 12px;
  min-height: 86px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.produto-detail-stat-label {
  font-size: 11px;
  font-weight: 900;
  color: #64748b;
}

.produto-detail-stat-value {
  font-size: 18px;
  line-height: 1.15;
  color: #0f172a;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.produto-detail-stat-helper {
  font-size: 11px;
  color: #64748b;
  font-weight: 760;
  line-height: 1.25;
}

.produto-detail-section {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.produto-detail-section-header {
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.produto-detail-section-header-with-tabs {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.produto-detail-section-title-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.produto-detail-section-title-block strong {
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.produto-detail-section-title-block span {
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.produto-detail-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
}

.produto-detail-tab {
  height: 31px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
  transition:
    background 0.16s ease,
    color 0.16s ease,
    border-color 0.16s ease,
    transform 0.16s ease;
}

.produto-detail-tab:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
}

.produto-detail-tab.is-active {
  border-color: #2f3f59;
  background: #2f3f59;
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(47, 63, 89, 0.18);
}

.produto-detail-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.produto-detail-field-box {
  border: 1px solid #edf2f7;
  background: #f8fafc;
  padding: 9px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.produto-detail-field-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.produto-detail-field-value {
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 900;
  overflow-wrap: anywhere;
}

.produto-detail-summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 9px;
}

.produto-detail-summary-card {
  --summary-bg: #f8fafc;
  --summary-border: #e2e8f0;
  --summary-color: #334155;
  --summary-strong: #0f172a;

  position: relative;
  overflow: hidden;
  border: 1px solid var(--summary-border);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.86), rgba(255, 255, 255, 0.66)),
    var(--summary-bg);
  padding: 10px;
  min-height: 124px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.produto-detail-summary-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--summary-color);
}

.produto-detail-summary-card.is-entrada {
  --summary-bg: #f0fdf4;
  --summary-border: #bbf7d0;
  --summary-color: #16a34a;
  --summary-strong: #166534;
}

.produto-detail-summary-card.is-saida {
  --summary-bg: #eff6ff;
  --summary-border: #bfdbfe;
  --summary-color: #2563eb;
  --summary-strong: #1d4ed8;
}

.produto-detail-summary-card.is-retorno {
  --summary-bg: #f0fdfa;
  --summary-border: #99f6e4;
  --summary-color: #0d9488;
  --summary-strong: #0f766e;
}

.produto-detail-summary-card.is-ajuste {
  --summary-bg: #fff7ed;
  --summary-border: #fed7aa;
  --summary-color: #f97316;
  --summary-strong: #9a3412;
}

.produto-detail-summary-card.is-perda {
  --summary-bg: #fef2f2;
  --summary-border: #fecaca;
  --summary-color: #dc2626;
  --summary-strong: #991b1b;
}

.produto-detail-summary-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  padding-left: 2px;
}

.produto-detail-summary-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.produto-detail-summary-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.produto-detail-summary-main strong {
  color: var(--summary-strong);
  font-size: 22px;
  line-height: 1;
  font-weight: 950;
  letter-spacing: -0.03em;
  overflow-wrap: anywhere;
}

.produto-detail-summary-main span {
  color: #64748b;
  font-size: 10.5px;
  font-weight: 850;
  line-height: 1.22;
}

.produto-detail-summary-secondary {
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.78);
  padding: 7px 8px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.produto-detail-summary-secondary strong {
  color: #0f172a;
  font-size: 14px;
  line-height: 1;
  font-weight: 950;
}

.produto-detail-summary-secondary span {
  color: #64748b;
  font-size: 10.5px;
  font-weight: 850;
  text-align: right;
  line-height: 1.15;
}

.produto-detail-price-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.produto-detail-price-summary-item {
  border: 1px solid #e9d5ff;
  background: linear-gradient(180deg, #faf9ff, #ffffff);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.produto-detail-price-summary-item span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.produto-detail-price-summary-item strong {
  color: #4c1d95;
  font-size: 15px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.produto-detail-history-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.produto-detail-history-card {
  --card-bg: #f8fafc;
  --card-border: #e2e8f0;
  --card-dot: #64748b;

  border: 1px solid var(--card-border);
  border-left: 4px solid var(--card-dot);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.66)),
    var(--card-bg);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.produto-detail-history-card.is-entrada {
  --card-bg: #f0fdf4;
  --card-border: #bbf7d0;
  --card-dot: #16a34a;
}

.produto-detail-history-card.is-saida {
  --card-bg: #f8fbff;
  --card-border: #bfdbfe;
  --card-dot: #2563eb;
}

.produto-detail-history-card.is-retorno {
  --card-bg: #f0fdfa;
  --card-border: #99f6e4;
  --card-dot: #0d9488;
}

.produto-detail-history-card.is-ajuste {
  --card-bg: #fffaf4;
  --card-border: #fed7aa;
  --card-dot: #f97316;
}

.produto-detail-history-card.is-perda {
  --card-bg: #fff7f7;
  --card-border: #fecaca;
  --card-dot: #dc2626;
}

.produto-detail-history-card.is-preco {
  --card-bg: #faf9ff;
  --card-border: #ddd6fe;
  --card-dot: #7c3aed;
}

.produto-detail-history-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.produto-detail-history-title-area {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.produto-detail-history-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 4px;
  flex-shrink: 0;
  background: #64748b;
}

.produto-detail-history-dot.is-entrada {
  background: #16a34a;
}

.produto-detail-history-dot.is-saida {
  background: #2563eb;
}

.produto-detail-history-dot.is-retorno {
  background: #0d9488;
}

.produto-detail-history-dot.is-ajuste {
  background: #f97316;
}

.produto-detail-history-dot.is-perda {
  background: #dc2626;
}

.produto-detail-history-dot.is-preco {
  background: #7c3aed;
}

.produto-detail-history-title-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.produto-detail-history-title-text strong {
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.produto-detail-history-title-text span {
  color: #64748b;
  font-size: 11.5px;
  font-weight: 760;
  overflow-wrap: anywhere;
}

.produto-detail-history-badge {
  min-height: 23px;
  padding: 0 8px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 950;
  white-space: nowrap;
  flex-shrink: 0;
}

.produto-detail-history-badge.is-entrada {
  background: #ecfdf3;
  color: #166534;
  border-color: #bbf7d0;
}

.produto-detail-history-badge.is-saida {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.produto-detail-history-badge.is-retorno {
  background: #f0fdfa;
  color: #0f766e;
  border-color: #99f6e4;
}

.produto-detail-history-badge.is-ajuste {
  background: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.produto-detail-history-badge.is-perda {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}

.produto-detail-history-badge.is-preco {
  background: #f5f3ff;
  color: #6d28d9;
  border-color: #ddd6fe;
}

.produto-detail-history-badge.is-neutro {
  background: #f8fafc;
  color: #475569;
  border-color: #cbd5e1;
}

.produto-detail-history-metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
}

.produto-detail-history-metric {
  border: 1px solid rgba(226, 232, 240, 0.92);
  background: rgba(255, 255, 255, 0.8);
  padding: 7px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.produto-detail-history-metric-label {
  color: #64748b;
  font-size: 10.5px;
  font-weight: 900;
}

.produto-detail-history-metric-value {
  color: #0f172a;
  font-size: 11.5px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.produto-detail-history-description {
  border: 1px solid rgba(226, 232, 240, 0.92);
  background: rgba(255, 255, 255, 0.72);
  color: #475569;
  padding: 8px 9px;
  font-size: 11.5px;
  font-weight: 760;
  line-height: 1.35;
}

.produto-detail-correction-bar {
  border: 1px solid #fed7aa;
  background: linear-gradient(180deg, #fffaf4, #fff7ed);
  padding: 11px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.produto-detail-correction-bar-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.produto-detail-correction-bar-text strong {
  color: #9a3412;
  font-size: 12.5px;
  font-weight: 950;
}

.produto-detail-correction-bar-text span {
  color: #7c2d12;
  font-size: 11.5px;
  font-weight: 760;
  line-height: 1.35;
}

.produto-detail-correction-button {
  min-height: 34px;
  border: 1px solid #c2410c;
  background: #c2410c;
  color: #ffffff;
  padding: 0 12px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.produto-detail-correction-button:hover:not(:disabled) {
  background: #9a3412;
  border-color: #9a3412;
}

.produto-detail-correction-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.produto-correction-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1750;
  background: rgba(15, 23, 42, 0.46);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.produto-correction-modal {
  width: min(760px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.28);
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

.produto-correction-source {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.produto-correction-source-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.produto-correction-source-title span {
  color: #64748b;
  font-size: 10.5px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.35px;
}

.produto-correction-source-title strong {
  color: #0f172a;
  font-size: 13px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.produto-correction-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.produto-correction-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: inherit;
}

.produto-correction-field > span {
  color: #334155;
  font-size: 12px;
  font-weight: 900;
}

.produto-correction-field select,
.produto-correction-field textarea {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  font-family: inherit;
  font-size: 13px;
  outline: none;
}

.produto-correction-field select {
  height: 38px;
  padding: 0 11px;
}

.produto-correction-field textarea {
  min-height: 82px;
  resize: vertical;
  padding: 10px 11px;
  line-height: 1.4;
}

.produto-correction-field select:disabled,
.produto-correction-field textarea:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.produto-correction-reason {
  margin-top: 16px;
}

.produto-correction-info {
  margin-top: 16px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1e3a8a;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
}

.produto-edit-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1700;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.produto-edit-modal {
  width: min(860px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.25);
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

.produto-edit-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
}

.produto-edit-modal-title {
  color: #0f172a;
  font-size: 17px;
  font-weight: 950;
}

.produto-edit-modal-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.produto-edit-modal-close {
  height: 34px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  padding: 0 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;
}

.produto-edit-modal-close:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.produto-edit-modal-body {
  padding: 22px;
}

.produto-edit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.produto-edit-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: inherit;
}

.produto-edit-field span {
  font-size: 12px;
  font-weight: 900;
  color: #334155;
}

.produto-edit-field input {
  height: 38px;
  border: 1px solid #cbd5e1;
  padding: 0 11px;
  font-family: inherit;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  outline: none;
}

.produto-edit-field input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.produto-edit-check-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.produto-edit-check {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  cursor: pointer;
  user-select: none;
}

.produto-edit-check input {
  margin-top: 3px;
}

.produto-edit-check strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 950;
}

.produto-edit-check span {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 760;
  line-height: 1.25;
}

.produto-edit-alert {
  margin-top: 16px;
  padding: 10px 12px;
  border-width: 1px;
  border-style: solid;
  font-size: 13px;
  font-weight: 850;
}

.produto-edit-alert.is-error {
  background: #fff1f1;
  color: #991b1b;
  border-color: #fecaca;
}

.produto-edit-alert.is-success {
  background: #f0fdf4;
  color: #166534;
  border-color: #bbf7d0;
}

.produto-edit-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.produto-edit-secondary-button,
.produto-edit-primary-button {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  font-family: inherit;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
}

.produto-edit-secondary-button {
  background: #ffffff;
  color: #334155;
}

.produto-edit-primary-button {
  border-color: #2f3f59;
  background: #2f3f59;
  color: #ffffff;
}

.produto-edit-secondary-button:disabled,
.produto-edit-primary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: ${MOBILE_BREAKPOINT}px) {
  .produto-detail-backdrop {
    top: 0;
    left: 0;
    z-index: 1400;
  }

  .produto-detail-panel {
    width: 100vw;
    height: 100vh;
  }
}

@media (max-width: 920px) {
  .produto-detail-stats-grid,
  .produto-detail-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .produto-detail-history-metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .produto-detail-price-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .produto-detail-panel {
    width: 100vw;
    height: 100vh;
  }

  .produto-detail-header {
    padding: 14px;
  }

  .produto-detail-title {
    font-size: 17px;
  }

  .produto-detail-header-actions {
    flex-direction: column-reverse;
    align-items: flex-end;
  }

  .produto-detail-action-button {
    height: 34px;
    padding: 0 10px;
    font-size: 11px;
  }

  .produto-detail-content {
    padding: 10px;
  }

  .produto-detail-stats-grid,
  .produto-detail-summary-grid,
  .produto-detail-field-grid,
  .produto-detail-history-metric-grid,
  .produto-detail-price-summary,
  .produto-edit-grid,
  .produto-edit-check-grid {
    grid-template-columns: 1fr;
  }

  .produto-detail-section-header-with-tabs {
    flex-direction: column;
  }

  .produto-detail-tabs {
    justify-content: flex-start;
  }

  .produto-detail-tab {
    flex: 1;
  }

  .produto-detail-correction-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .produto-detail-correction-button {
    width: 100%;
  }

  .produto-correction-backdrop {
    padding: 10px;
  }

  .produto-correction-modal {
    max-height: calc(100vh - 20px);
  }

  .produto-correction-grid {
    grid-template-columns: 1fr;
  }

  .produto-edit-backdrop {
    padding: 10px;
  }

  .produto-edit-modal {
    max-height: calc(100vh - 20px);
  }

  .produto-edit-modal-header {
    padding: 14px;
  }

  .produto-edit-modal-body {
    padding: 14px;
  }

  .produto-edit-footer {
    flex-direction: column-reverse;
  }

  .produto-edit-secondary-button,
  .produto-edit-primary-button {
    width: 100%;
  }
}
`;

function getStoredAccessToken(): string {
  try {
    for (const key of ACCESS_TOKEN_KEYS) {
      const value = localStorage.getItem(key);
      if (value && value.trim()) return value.trim();
    }
  } catch {
    // ignora indisponibilidade do localStorage
  }

  return "";
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const accessToken = getStoredAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

async function readJsonSafe<T>(res: Response): Promise<T | null> {
  const text = await res.text();

  if (!text) return null;

  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function normalizeCurrency(value: unknown): CurrencyCode | null {
  const s = String(value ?? "")
    .trim()
    .toUpperCase();

  if (s === "BRL" || s === "USD" || s === "EUR") return s;

  return null;
}

function formatCurrencyIntl(
  value: string | number | null | undefined,
  currency?: CurrencyCode | null
): string {
  if (value === null || value === undefined || value === "") return "—";

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(n)) return "—";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(n);
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return iso;

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${data} - ${hora}`;
  } catch {
    return iso || "—";
  }
}

function isDetalhePayload(value: unknown): value is ProdutoDetalhePayload {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "produto" in value
  );
}

function normalizeProdutoFromResponse(
  json: GetProdutoResp,
  codigo: string
): ProdutoDetalhePayload | null {
  const code = String(codigo || "")
    .trim()
    .toUpperCase();

  if (isDetalhePayload(json.data)) {
    return json.data;
  }

  const produtos = Array.isArray(json.data)
    ? json.data
    : Array.isArray(json.visiveis)
    ? json.visiveis
    : [];

  const produto =
    produtos.find(
      (p) =>
        String(p.codigo || "")
          .trim()
          .toUpperCase() === code
    ) ?? produtos[0];

  if (!produto) return null;

  return {
    produto,
    custos: [],
    movimentos_por_tipo: {},
    estoque_atual: {
      produto_id: produto.id ?? produto.produto_id ?? null,
      estoque_id: produto.estoque_id ?? json.estoque?.id ?? null,
      quantidade_atual: produto.quantidade_atual ?? null,
      custo_unitario_atual:
        produto.custo_unitario_atual ??
        produto.custo_unitario ??
        produto.ultimo_custo_unitario ??
        null,
      moeda: produto.moeda ?? produto.ultima_moeda ?? null,
    },
  };
}

function getMovimentoDate(item: ApiMovItem | null | undefined): string | null {
  if (!item) return null;
  return item.data_movimentacao || item.criado_em || null;
}

function getMovimentoTimestamp(item: ApiMovItem | null | undefined): number {
  const data = getMovimentoDate(item);
  if (!data) return 0;

  const t = Date.parse(data);
  return Number.isFinite(t) ? t : 0;
}

function normalizeTextKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function normalizeMovementRaw(item: ApiMovItem): string {
  return `${item.tipo || ""} ${item.tipo_full || ""} ${
    item.codigo_movimentacao || ""
  } ${item.tipo_codigo || ""} ${item.tipo_descricao || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function getMovementTone(item: ApiMovItem): HistoryTone {
  const raw = normalizeMovementRaw(item);

  if (raw.includes("preco") || raw.includes("price")) return "preco";

  /*
    ENT-AJUSTE é código válido no banco, mas visualmente deve ser tratado como ajuste.
    Por isso "ajuste" vem antes de "entrada" e "ent-".
  */
  if (raw.includes("ajuste")) return "ajuste";

  if (raw.includes("retorno") || raw.includes("ret-")) return "retorno";
  if (raw.includes("perda") || raw.includes("baixa")) return "perda";
  if (raw.includes("entrada") || raw.includes("ent-")) return "entrada";

  if (
    raw.includes("saida") ||
    raw.includes("servico") ||
    raw.includes("venda") ||
    raw.includes("consumo") ||
    raw.includes("sai-")
  ) {
    return "saida";
  }

  return "neutro";
}

function getQuantityTone(item: ApiMovItem): HistoryTone {
  const tone = getMovementTone(item);

  if (tone !== "neutro") return tone;

  const qtd = toNumberOrNull(item.quantidade);

  if (qtd !== null && qtd > 0) return "entrada";
  if (qtd !== null && qtd < 0) return "saida";

  return "neutro";
}

function normalizeMovimentoItem(item: ApiMovItem): ApiMovItem {
  const data =
    item.data_movimentacao ?? item.criado_em ?? (item as any).data ?? null;

  const tone = getMovementTone(item);

  let tipo = item.tipo || item.tipo_full || "Movimentação";

  if (tone === "preco") tipo = "Ajuste de Preço";
  else if (tone === "ajuste") tipo = "Ajuste";
  else if (tone === "retorno") tipo = "Retorno";
  else if (tone === "perda") tipo = "Perda";
  else if (tone === "entrada") tipo = "Entrada";
  else if (tone === "saida") tipo = "Saída";

  return {
    ...item,
    tipo,
    data_movimentacao: data,
  };
}

function isPriceMovement(item: ApiMovItem): boolean {
  const raw = normalizeMovementRaw(item);
  return raw.includes("preco") || raw.includes("price");
}

function isMovimentoDerivadoFront(item: ApiMovItem): boolean {
  const tipo = String(item.tipo || "")
    .trim()
    .toLowerCase();

  const tipoFull = String(item.tipo_full || "")
    .trim()
    .toLowerCase();

  const codigoMov = String(item.codigo_movimentacao || "")
    .trim()
    .toUpperCase();

  const tipoCodigo = String(item.tipo_codigo || "")
    .trim()
    .toUpperCase();

  const origemMovId = item.origem_mov_id;
  const base = `${tipo} ${tipoFull}`.toLowerCase();

  const temOrigem =
    origemMovId !== null &&
    origemMovId !== undefined &&
    String(origemMovId).trim() !== "" &&
    String(origemMovId).trim() !== "0";

  const isRetorno =
    base.includes("retorno") ||
    codigoMov.includes("RETORNO") ||
    tipoCodigo.includes("RETORNO") ||
    codigoMov === "RET-ESTOQUE" ||
    tipoCodigo === "RET-ESTOQUE";

  if (isRetorno) return false;

  const isPerda =
    codigoMov === "BAIXA-PERDA" ||
    codigoMov === "SAI-PERDA" ||
    tipoCodigo === "BAIXA-PERDA" ||
    tipoCodigo === "SAI-PERDA" ||
    tipo === "perda" ||
    tipoFull === "perda" ||
    base.includes("perda");

  if (isPerda) return false;

  const isAjuste = base.includes("ajuste") || codigoMov.includes("AJUSTE");

  if (isAjuste) return false;

  const isCancelamento =
    base.includes("cancelamento") ||
    base.includes("cancelado") ||
    codigoMov.includes("CANCEL") ||
    tipoCodigo.includes("CANCEL");

  if (isCancelamento) return true;

  return temOrigem;
}

function extractMovimentos(
  payload: ProdutoDetalhePayload | null
): ApiMovItem[] {
  const mov = payload?.movimentos_por_tipo;

  if (!mov) return [];

  const all = Object.values(mov).flatMap((arr) =>
    Array.isArray(arr) ? arr : []
  ) as ApiMovItem[];

  return all
    .filter((item) => !isMovimentoDerivadoFront(item))
    .filter((item) => !isPriceMovement(item))
    .map(normalizeMovimentoItem)
    .sort((a, b) => getMovimentoTimestamp(b) - getMovimentoTimestamp(a));
}

function extractPrecos(payload: ProdutoDetalhePayload | null): ApiMovItem[] {
  const custos = Array.isArray(payload?.custos) ? payload?.custos ?? [] : [];
  const mov = payload?.movimentos_por_tipo;

  const allMov = mov
    ? (Object.values(mov).flatMap((arr) =>
        Array.isArray(arr) ? arr : []
      ) as ApiMovItem[])
    : [];

  const ajustesPreco = allMov.filter(isPriceMovement);

  return [...custos, ...ajustesPreco]
    .map((item) => ({
      ...normalizeMovimentoItem(item),
      tipo: isPriceMovement(item) ? "Ajuste de Preço" : item.tipo || "Preço",
    }))
    .sort((a, b) => getMovimentoTimestamp(b) - getMovimentoTimestamp(a));
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : null;
}

function formatQuantidade(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(n)) return String(value);

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 4,
  }).format(n);
}

function formatSignedQuantidade(value: unknown): string {
  const n = toNumberOrNull(value);

  if (n === null) return "—";
  if (n > 0) return `+${formatQuantidade(n)}`;

  return formatQuantidade(n);
}

function parseQuantidadeAjuste(value: string): number | null {
  const raw = String(value ?? "").trim();

  if (!raw) return null;

  const n = Number(raw.replace(",", "."));

  if (!Number.isInteger(n) || n < 0) return NaN;

  return n;
}

function parseCurrencyValue(value: string): number | null {
  let raw = String(value ?? "").trim();

  if (!raw) return null;

  raw = raw.replace(/\s/g, "");

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  if (hasComma && hasDot) {
    if (raw.lastIndexOf(",") > raw.lastIndexOf(".")) {
      raw = raw.replace(/\./g, "").replace(",", ".");
    } else {
      raw = raw.replace(/,/g, "");
    }
  } else if (hasComma) {
    raw = raw.replace(",", ".");
  }

  const n = Number(raw);

  if (!Number.isFinite(n) || n < 0) return NaN;

  return n;
}

function parseJsonRecord(value: unknown): Record<string, unknown> | null {
  if (!value) return null;

  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }

  return null;
}

function getMovimentoMeta(item: ApiMovItem): Record<string, unknown> {
  return {
    ...(parseJsonRecord(item.metadados_origem) || {}),
    ...(parseJsonRecord(item.metadados) || {}),
    ...(parseJsonRecord(item.metadata) || {}),
  };
}

function getFirstNumberFromSources(
  item: ApiMovItem,
  meta: Record<string, unknown>,
  keys: string[]
): number | null {
  for (const key of keys) {
    const direct = toNumberOrNull((item as any)[key]);

    if (direct !== null) return direct;

    const fromMeta = toNumberOrNull(meta[key]);

    if (fromMeta !== null) return fromMeta;
  }

  return null;
}

function getAjusteQuantidadeInfo(item: ApiMovItem): {
  anterior: number | null;
  definida: number | null;
  delta: number | null;
} {
  const meta = getMovimentoMeta(item);

  let anterior = getFirstNumberFromSources(item, meta, [
    "quantidade_anterior",
    "saldo_anterior",
    "estoque_anterior",
    "quantidadeAnterior",
  ]);

  let definida = getFirstNumberFromSources(item, meta, [
    "quantidade_nova",
    "quantidade_definida",
    "quantidade_atual",
    "saldo_definido",
    "saldo_atual",
    "estoque_atual",
    "quantidadeNova",
    "quantidadeDefinida",
  ]);

  let delta = getFirstNumberFromSources(item, meta, [
    "delta",
    "quantidade_delta",
    "ajuste",
  ]);

  if (delta === null) {
    delta = toNumberOrNull(item.quantidade);
  }

  /*
    Fallback para o motivo gravado pela Lambda:
    "Ajuste manual de saldo pela Diretoria: 10 -> 15"
  */
  const motivo = String(item.motivo || "");
  const parsedMotivo = motivo.match(
    /(-?\d+(?:[,.]\d+)?)\s*[-=]*>\s*(-?\d+(?:[,.]\d+)?)/i
  );

  if (parsedMotivo) {
    if (anterior === null) anterior = toNumberOrNull(parsedMotivo[1]);
    if (definida === null) definida = toNumberOrNull(parsedMotivo[2]);
  }

  if (definida === null && anterior !== null && delta !== null) {
    definida = anterior + delta;
  }

  if (anterior === null && definida !== null && delta !== null) {
    anterior = definida - delta;
  }

  return {
    anterior,
    definida,
    delta,
  };
}

async function getProdutoDetalhe(
  apiBase: string,
  moduloAtual: ModuloAtual | null,
  codigo: string,
  signal?: AbortSignal
): Promise<ProdutoDetalhePayload> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota.");
  }

  if (!codigo) {
    throw new Error("Código do produto não informado.");
  }

  const normalizedApiBase = String(apiBase || API_LOGVI_FALLBACK_BASE).replace(
    /\/+$/,
    ""
  );

  const urlGetProduto = `${normalizedApiBase}/template/estoque-administrativo/get-produto`;

  const res = await fetch(urlGetProduto, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    signal,
    body: JSON.stringify({
      modulo_id: moduloAtual.id,
      codigo,
      modo: "detalhe",
      include_historico: true,
    }),
  });

  const json = (await readJsonSafe<GetProdutoResp>(res)) ?? {
    ok: res.ok,
    data: null,
  };

  if (!res.ok || json.ok === false) {
    const msg =
      json.erro ||
      (Array.isArray(json.erros) && json.erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(msg);
  }

  const normalized = normalizeProdutoFromResponse(json, codigo);

  if (!normalized) {
    throw new Error("Produto não encontrado.");
  }

  return normalized;
}

async function postEditarProduto(
  apiBase: string,
  moduloAtual: ModuloAtual | null,
  produtoId: number,
  form: EditFormState,
  permitirCamposDiretoria: boolean
): Promise<void> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota.");
  }

  const normalizedApiBase = String(apiBase || API_LOGVI_FALLBACK_BASE).replace(
    /\/+$/,
    ""
  );

  const urlEditarProduto = `${normalizedApiBase}/template/estoque-administrativo/produtos-editar`;

  const codigoEditado = form.codigo.trim().toUpperCase();
  const nomeEditado = form.nome.trim();

  if (!codigoEditado) {
    throw new Error("O código do produto é obrigatório.");
  }

  if (!nomeEditado) {
    throw new Error("O nome do produto é obrigatório.");
  }

  const body: Record<string, unknown> = {
    modulo_id: moduloAtual.id,
    id: produtoId,
    codigo: codigoEditado,
    nome: nomeEditado,
    ncm: form.ncm.trim() || null,
    classificacao: form.classificacao.trim() || null,
    posicao: form.posicao.trim() || null,
    peso: form.peso.trim() || null,
    dimensao: form.dimensao.trim() || null,
  };

  if (permitirCamposDiretoria) {
    body.vendavel = form.vendavel;

    const rawQuantidadeAtual = form.quantidadeAtual.trim();

    if (rawQuantidadeAtual) {
      const quantidadeAtualEditada = parseQuantidadeAjuste(rawQuantidadeAtual);

      if (
        quantidadeAtualEditada === null ||
        Number.isNaN(quantidadeAtualEditada)
      ) {
        throw new Error(
          "A quantidade atual deve ser um número inteiro maior ou igual a zero."
        );
      }

      body.quantidade_atual = quantidadeAtualEditada;
    }
  }

  const res = await fetch(urlEditarProduto, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(body),
  });

  const json = await readJsonSafe<PostEditarProdutoResp>(res);

  if (!res.ok || json?.ok === false) {
    const msg =
      json?.erro ||
      (Array.isArray(json?.erros) ? json?.erros.join(" | ") : "") ||
      `HTTP ${res.status}`;

    throw new Error(msg);
  }
}

async function postCorrigirUltimaMovimentacao(
  apiBase: string,
  moduloAtual: ModuloAtual | null,
  produtoId: number,
  estoqueId: number | null,
  movimentoOriginal: ApiMovItem,
  moedaAtualAplicada: CurrencyCode,
  custoAtualAplicado: string | number | null | undefined,
  form: CorrectionFormState
): Promise<void> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota.");
  }

  if (!estoqueId) {
    throw new Error("Estoque atual não identificado para a correção.");
  }

  const movimentacaoId = toNumberOrNull(movimentoOriginal.movimentacao_id);
  const movimentacaoItemId = toNumberOrNull(
    movimentoOriginal.item_id ?? movimentoOriginal.id ?? null
  );

  if (!movimentacaoId && !movimentacaoItemId) {
    throw new Error(
      "A última movimentação não possui identificação suficiente para correção."
    );
  }

  // A comparação é feita contra o estado ATUAL do estoque, e não contra o
  // registro histórico original. Isso permite uma segunda correção sem
  // reescrever a movimentação original.
  const moedaAnterior =
    normalizeCurrency(moedaAtualAplicada) ||
    normalizeCurrency(movimentoOriginal.moeda);
  const custoAnterior =
    toNumberOrNull(custoAtualAplicado) ??
    toNumberOrNull(movimentoOriginal.custo_unitario);

  const moedaNova = normalizeCurrency(form.moeda);
  const custoNovo = parseCurrencyValue(form.custoUnitario);

  if (!moedaNova) {
    throw new Error("Selecione uma moeda válida para a correção.");
  }

  if (custoNovo === null || Number.isNaN(custoNovo)) {
    throw new Error("Informe um custo unitário válido, maior ou igual a zero.");
  }

  const mudouMoeda = moedaAnterior !== moedaNova;
  const mudouCusto =
    custoAnterior === null || Math.abs(custoAnterior - custoNovo) > 0.0000001;

  if (!mudouMoeda && !mudouCusto) {
    throw new Error(
      "Nenhuma alteração foi informada. Modifique a moeda e/ou o custo unitário."
    );
  }

  const motivo = form.motivo.trim();

  if (!motivo) {
    throw new Error("Informe o motivo da correção.");
  }

  const normalizedApiBase = String(apiBase || API_LOGVI_FALLBACK_BASE).replace(
    /\/+$/,
    ""
  );

  // A correção usa o MESMO endpoint de edição/ajuste do produto.
  const urlEditarProduto = `${normalizedApiBase}/template/estoque-administrativo/produtos-editar`;

  const body = {
    acao: "corrigir-ultima-movimentacao",
    modo: "correcao-preco-moeda",

    modulo_id: moduloAtual.id,
    id: produtoId,
    produto_id: produtoId,
    estoque_id: estoqueId,

    movimentacao_id: movimentacaoId,
    movimentacao_item_id: movimentacaoItemId,
    item_id: movimentacaoItemId,

    validar_como_ultima_movimentacao: true,
    criar_movimentacao_ajuste: true,

    // Estado que a tela encontrou ao abrir/salvar. O backend compara com o
    // estoque bloqueado para impedir correção sobre estado já alterado.
    moeda_anterior: moedaAnterior,
    custo_unitario_anterior: custoAnterior,

    // Novo estado. Não é enviado nenhum campo de quantidade.
    moeda: moedaNova,
    moeda_nova: moedaNova,
    custo_unitario: custoNovo,
    custo_unitario_novo: custoNovo,

    motivo,
  };

  const res = await fetch(urlEditarProduto, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(body),
  });

  const json = await readJsonSafe<PostCorrigirMovimentacaoResp>(res);

  if (!res.ok || json?.ok === false) {
    const msg =
      json?.erro ||
      (Array.isArray(json?.erros) ? json?.erros.join(" | ") : "") ||
      `HTTP ${res.status}`;

    throw new Error(msg);
  }
}

function getHistoryToneLabel(tone: HistoryTone): string {
  switch (tone) {
    case "entrada":
      return "Entrada";
    case "saida":
      return "Saída";
    case "retorno":
      return "Retorno";
    case "ajuste":
      return "Ajuste";
    case "perda":
      return "Perda";
    case "preco":
      return "Preço";
    default:
      return "Movimentação";
  }
}

function getItemEstimatedValue(item: ApiMovItem): number {
  const qtd = Math.abs(toNumberOrNull(item.quantidade) || 0);
  const custo = toNumberOrNull(item.custo_unitario) || 0;

  return qtd * custo;
}

function getMovementTitle(item: ApiMovItem, fallback: string) {
  const tipo = String(item.tipo || "").trim();
  const tipoFull = String(item.tipo_full || "").trim();

  if (tipo) return tipo;
  if (tipoFull) return tipoFull;

  return fallback;
}

function getMovementCode(item: ApiMovItem) {
  return (
    item.codigo_movimentacao || item.tipo_codigo || item.tipo_descricao || null
  );
}

function getHistoryDescription(item: ApiMovItem) {
  const tone = getMovementTone(item);
  const ajusteInfo = tone === "ajuste" ? getAjusteQuantidadeInfo(item) : null;

  const ajusteDescricao =
    ajusteInfo && ajusteInfo.anterior !== null && ajusteInfo.definida !== null
      ? `Saldo ajustado de ${formatQuantidade(
          ajusteInfo.anterior
        )} para ${formatQuantidade(ajusteInfo.definida)}`
      : null;

  return [
    ajusteDescricao,
    item.motivo,
    item.os_numero ? `OS ${item.os_numero}` : null,
    item.proposta_venda ? `Proposta ${item.proposta_venda}` : null,
    item.tipo_descricao,
  ]
    .filter(Boolean)
    .join(" · ");
}

function createResumoBucket(): MovimentosResumoBucket {
  return {
    movimentacoes: 0,
    quantidadeTotal: 0,
  };
}

function createMovimentosResumo(): MovimentosResumo {
  return {
    entradas: createResumoBucket(),
    saidas: createResumoBucket(),
    retornos: createResumoBucket(),
    ajustes: createResumoBucket(),
    perdas: createResumoBucket(),
    outros: createResumoBucket(),
  };
}

function addResumoItem(bucket: MovimentosResumoBucket, item: ApiMovItem) {
  const qtd = toNumberOrNull(item.quantidade);

  bucket.movimentacoes += 1;

  if (qtd !== null) {
    bucket.quantidadeTotal += Math.abs(qtd);
  }
}

const DetailStat: React.FC<{
  label: string;
  value: string;
  helper?: string;
}> = ({ label, value, helper }) => (
  <div className="produto-detail-stat-card">
    <span className="produto-detail-stat-label">{label}</span>
    <strong className="produto-detail-stat-value">{value}</strong>
    {helper && <span className="produto-detail-stat-helper">{helper}</span>}
  </div>
);

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="produto-detail-field-box">
    <span className="produto-detail-field-label">{label}</span>
    <strong className="produto-detail-field-value">{value || "—"}</strong>
  </div>
);

const EditField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  type?: React.HTMLInputTypeAttribute;
  min?: string;
  step?: string;
  onChange: (value: string) => void;
}> = ({
  label,
  value,
  disabled,
  inputMode,
  type = "text",
  min,
  step,
  onChange,
}) => (
  <label className="produto-edit-field">
    <span>{label}</span>

    <input
      type={type}
      min={min}
      step={step}
      inputMode={inputMode}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const EditCheck: React.FC<{
  checked: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  onChange: (checked: boolean) => void;
}> = ({ checked, disabled, title, description, onChange }) => (
  <label className="produto-edit-check">
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />

    <span>
      <strong>{title}</strong>
      <span>{description}</span>
    </span>
  </label>
);

const HistoryBadge: React.FC<{
  children: React.ReactNode;
  tone: HistoryTone;
}> = ({ children, tone }) => (
  <span className={`produto-detail-history-badge is-${tone}`}>{children}</span>
);

const HistoryMetric: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => (
  <div className="produto-detail-history-metric">
    <span className="produto-detail-history-metric-label">{label}</span>
    <strong className="produto-detail-history-metric-value">{value}</strong>
  </div>
);

const HistorySummaryItem: React.FC<{
  label: string;
  tone: HistoryTone;
  movimentacoes: number;
  quantidadeTotal: number;
}> = ({ label, tone, movimentacoes, quantidadeTotal }) => (
  <div className={`produto-detail-summary-card is-${tone}`}>
    <div className="produto-detail-summary-header">
      <HistoryBadge tone={tone}>{label}</HistoryBadge>
    </div>

    <div className="produto-detail-summary-body">
      <div className="produto-detail-summary-main">
        <strong>{formatQuantidade(quantidadeTotal)}</strong>
        <span>Qtd. total movimentada</span>
      </div>

      <div className="produto-detail-summary-secondary">
        <strong>{movimentacoes}</strong>
        <span>{movimentacoes === 1 ? "movimentação" : "movimentações"}</span>
      </div>
    </div>
  </div>
);

const MovementHistoryCard: React.FC<{
  item: ApiMovItem;
  index: number;
  moedaAtual: CurrencyCode;
}> = ({ item, index, moedaAtual }) => {
  const tone = getQuantityTone(item);
  const moeda = normalizeCurrency(item.moeda) || moedaAtual;
  const qtd = toNumberOrNull(item.quantidade);
  const valorEstimado = getItemEstimatedValue(item);
  const titulo = getMovementTitle(item, getHistoryToneLabel(tone));
  const codigo = getMovementCode(item);
  const descricao = getHistoryDescription(item);
  const ajusteInfo = tone === "ajuste" ? getAjusteQuantidadeInfo(item) : null;

  return (
    <div
      key={`${item.id || item.item_id || item.movimentacao_id || index}`}
      className={`produto-detail-history-card is-${tone}`}
    >
      <div className="produto-detail-history-card-top">
        <div className="produto-detail-history-title-area">
          <div className={`produto-detail-history-dot is-${tone}`} />

          <div className="produto-detail-history-title-text">
            <strong>{titulo}</strong>
            <span>
              {formatDateTime(getMovimentoDate(item))}
              {codigo ? ` · ${codigo}` : ""}
            </span>
          </div>
        </div>

        <HistoryBadge tone={tone}>{getHistoryToneLabel(tone)}</HistoryBadge>
      </div>

      {tone === "ajuste" ? (
        <div className="produto-detail-history-metric-grid">
          <HistoryMetric
            label="Ajuste aplicado"
            value={formatSignedQuantidade(ajusteInfo?.delta ?? qtd)}
          />

          <HistoryMetric
            label="Quantidade definida"
            value={formatQuantidade(ajusteInfo?.definida)}
          />

          <HistoryMetric
            label="Quantidade anterior"
            value={formatQuantidade(ajusteInfo?.anterior)}
          />

          <HistoryMetric
            label="Responsável"
            value={item.responsavel_nome || "—"}
          />
        </div>
      ) : (
        <div className="produto-detail-history-metric-grid">
          <HistoryMetric
            label="Quantidade"
            value={qtd === null ? "—" : formatQuantidade(qtd)}
          />

          <HistoryMetric
            label="Custo unitário"
            value={formatCurrencyIntl(item.custo_unitario, moeda)}
          />

          <HistoryMetric
            label="Valor total (custo x quantidade)"
            value={formatCurrencyIntl(valorEstimado, moeda)}
          />

          <HistoryMetric
            label="Responsável"
            value={item.responsavel_nome || "—"}
          />
        </div>
      )}

      {descricao && (
        <div className="produto-detail-history-description">{descricao}</div>
      )}
    </div>
  );
};

const PriceHistoryCard: React.FC<{
  item: ApiMovItem;
  index: number;
  moedaAtual: CurrencyCode;
}> = ({ item, index, moedaAtual }) => {
  const moeda = normalizeCurrency(item.moeda) || moedaAtual;
  const titulo = getMovementTitle(item, "Atualização de preço");

  const descricao = [
    item.motivo,
    item.codigo_movimentacao || item.tipo_codigo,
    item.tipo_descricao,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      key={`${item.id || item.item_id || item.movimentacao_id || index}`}
      className="produto-detail-history-card is-preco"
    >
      <div className="produto-detail-history-card-top">
        <div className="produto-detail-history-title-area">
          <div className="produto-detail-history-dot is-preco" />

          <div className="produto-detail-history-title-text">
            <strong>{titulo}</strong>
            <span>{formatDateTime(getMovimentoDate(item))}</span>
          </div>
        </div>

        <HistoryBadge tone="preco">Preço</HistoryBadge>
      </div>

      <div className="produto-detail-history-metric-grid">
        <HistoryMetric
          label="Custo registrado"
          value={formatCurrencyIntl(item.custo_unitario, moeda)}
        />

        <HistoryMetric label="Moeda" value={moeda} />

        <HistoryMetric
          label="Responsável"
          value={item.responsavel_nome || "—"}
        />

        <HistoryMetric
          label="Origem"
          value={
            item.codigo_movimentacao ||
            item.tipo_codigo ||
            item.tipo_full ||
            "—"
          }
        />
      </div>

      {descricao && (
        <div className="produto-detail-history-description">{descricao}</div>
      )}
    </div>
  );
};

const ProdutoDetailSidePanel: React.FC<{
  open: boolean;
  codigo: string | null;
  moduloAtual: ModuloAtual | null;
  areaTitle: string;
  onClose: () => void;
}> = ({ open, codigo, moduloAtual, areaTitle, onClose }) => {
  const { apiBase, sessao } = useAuth();

  const resolvedApiBase = useMemo(
    () => String(apiBase || API_LOGVI_FALLBACK_BASE).replace(/\/+$/, ""),
    [apiBase]
  );

  const isUsuarioDiretoria = useMemo(() => {
    return normalizeTextKey(sessao?.departamento) === "diretoria";
  }, [sessao?.departamento]);

  const [activeTab, setActiveTab] = useState<"mov" | "precos">("mov");
  const [payload, setPayload] = useState<ProdutoDetalhePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showEditOverlay, setShowEditOverlay] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  const [showCorrectionOverlay, setShowCorrectionOverlay] = useState(false);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionSuccess, setCorrectionSuccess] = useState<string | null>(
    null
  );
  const [correctionSource, setCorrectionSource] = useState<ApiMovItem | null>(
    null
  );
  const [correctionForm, setCorrectionForm] = useState<CorrectionFormState>({
    moeda: "BRL",
    custoUnitario: "",
    motivo: "Correção da última movimentação",
  });

  const [editForm, setEditForm] = useState<EditFormState>({
    codigo: "",
    nome: "",
    ncm: "",
    classificacao: "",
    posicao: "",
    vendavel: false,
    peso: "",
    dimensao: "",
    quantidadeAtual: "",
  });

  const codigoLimpo = useMemo(() => String(codigo || "").trim(), [codigo]);

  const carregarDetalhe = useCallback(
    async (signal?: AbortSignal) => {
      if (!codigoLimpo) {
        setPayload(null);
        setLoading(false);
        setError("Código do produto não informado.");
        return;
      }

      setPayload(null);
      setError(null);
      setLoading(true);

      try {
        const detalhe = await getProdutoDetalhe(
          resolvedApiBase,
          moduloAtual,
          codigoLimpo,
          signal
        );

        setPayload(detalhe);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Falha ao carregar detalhamento do produto.");
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [codigoLimpo, moduloAtual, resolvedApiBase]
  );

  useEffect(() => {
    if (open) setActiveTab("mov");
  }, [open, codigo]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showCorrectionOverlay) {
          if (!correctionLoading) {
            setShowCorrectionOverlay(false);
            setCorrectionError(null);
            setCorrectionSuccess(null);
            setCorrectionSource(null);
          }

          return;
        }

        if (showEditOverlay) {
          if (!editLoading) {
            setShowEditOverlay(false);
            setEditError(null);
            setEditSuccess(null);
          }

          return;
        }

        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    open,
    showCorrectionOverlay,
    correctionLoading,
    showEditOverlay,
    editLoading,
    onClose,
  ]);

  useEffect(() => {
    if (!open) {
      setPayload(null);
      setLoading(false);
      setError(null);
      setShowEditOverlay(false);
      setEditLoading(false);
      setEditError(null);
      setEditSuccess(null);
      setShowCorrectionOverlay(false);
      setCorrectionLoading(false);
      setCorrectionError(null);
      setCorrectionSuccess(null);
      setCorrectionSource(null);
      setCorrectionForm({
        moeda: "BRL",
        custoUnitario: "",
        motivo: "Correção da última movimentação",
      });
      return;
    }

    const controller = new AbortController();

    carregarDetalhe(controller.signal);

    return () => controller.abort();
  }, [open, codigoLimpo, moduloAtual?.id, resolvedApiBase, carregarDetalhe]);

  const onRetry = () => {
    const controller = new AbortController();
    carregarDetalhe(controller.signal);
  };

  const produto = payload?.produto ?? null;

  const produtoId = useMemo(() => {
    return toNumberOrNull(produto?.id ?? produto?.produto_id ?? null);
  }, [produto?.id, produto?.produto_id]);

  const estoqueId = useMemo(() => {
    return toNumberOrNull(
      payload?.estoque_atual?.estoque_id ?? produto?.estoque_id ?? null
    );
  }, [payload?.estoque_atual?.estoque_id, produto?.estoque_id]);

  const moedaAtual =
    normalizeCurrency(payload?.estoque_atual?.moeda) ||
    normalizeCurrency(produto?.moeda) ||
    normalizeCurrency(produto?.ultima_moeda) ||
    "BRL";

  const quantidadeAtual =
    payload?.estoque_atual?.quantidade_atual ??
    produto?.quantidade_atual ??
    null;

  const custoAtual =
    payload?.estoque_atual?.custo_unitario_atual ??
    produto?.custo_unitario_atual ??
    produto?.custo_unitario ??
    produto?.ultimo_custo_unitario ??
    null;

  const movimentos = extractMovimentos(payload);
  const precos = extractPrecos(payload);

  const nome = produto?.nome || "Produto";
  const codigoProduto = produto?.codigo || codigo || "—";
  const estoqueNome = produto?.estoque_nome || moduloAtual?.label || "Estoque";
  const ultimoMovimento = movimentos[0] || null;
  const ultimoPreco = precos[0] || null;

  const movimentosResumo = movimentos.reduce<MovimentosResumo>((acc, item) => {
    const tone = getQuantityTone(item);

    if (tone === "entrada") {
      addResumoItem(acc.entradas, item);
    } else if (tone === "saida") {
      addResumoItem(acc.saidas, item);
    } else if (tone === "retorno") {
      addResumoItem(acc.retornos, item);
    } else if (tone === "ajuste") {
      addResumoItem(acc.ajustes, item);
    } else if (tone === "perda") {
      addResumoItem(acc.perdas, item);
    } else {
      addResumoItem(acc.outros, item);
    }

    return acc;
  }, createMovimentosResumo());

  function handleOpenCorrectionOverlay() {
    if (!ultimoMovimento) return;

    const moedaBaseCorrecao =
      normalizeCurrency(moedaAtual) ||
      normalizeCurrency(ultimoMovimento.moeda) ||
      "BRL";
    const custoBaseCorrecao =
      toNumberOrNull(custoAtual) ??
      toNumberOrNull(ultimoMovimento.custo_unitario);

    setCorrectionError(null);
    setCorrectionSuccess(null);
    setCorrectionSource({ ...ultimoMovimento });
    setCorrectionForm({
      moeda: moedaBaseCorrecao,
      custoUnitario:
        custoBaseCorrecao !== null ? String(custoBaseCorrecao) : "",
      motivo: "Correção da última movimentação",
    });
    setShowCorrectionOverlay(true);
  }

  function handleCloseCorrectionOverlay() {
    if (correctionLoading) return;

    setShowCorrectionOverlay(false);
    setCorrectionError(null);
    setCorrectionSuccess(null);
    setCorrectionSource(null);
  }

  async function handleSubmitCorrection() {
    if (!isUsuarioDiretoria) {
      setCorrectionError(
        "Somente usuários da Diretoria podem corrigir a última movimentação."
      );
      return;
    }

    if (!produtoId) {
      setCorrectionError("Produto sem ID numérico válido para correção.");
      return;
    }

    if (!correctionSource) {
      setCorrectionError("Movimentação original não identificada.");
      return;
    }

    setCorrectionLoading(true);
    setCorrectionError(null);
    setCorrectionSuccess(null);

    try {
      await postCorrigirUltimaMovimentacao(
        resolvedApiBase,
        moduloAtual,
        produtoId,
        estoqueId,
        correctionSource,
        moedaAtual,
        custoAtual,
        correctionForm
      );

      setCorrectionSuccess(
        "Correção registrada. A movimentação original foi preservada e um ajuste foi criado."
      );

      await carregarDetalhe();

      window.setTimeout(() => {
        setShowCorrectionOverlay(false);
        setCorrectionSuccess(null);
        setCorrectionSource(null);
      }, 900);
    } catch (e: any) {
      setCorrectionError(
        e?.message || "Falha ao registrar a correção da movimentação."
      );
    } finally {
      setCorrectionLoading(false);
    }
  }

  function handleOpenEditOverlay() {
    if (!produto) return;

    setEditError(null);
    setEditSuccess(null);

    setEditForm({
      codigo: String(produto.codigo ?? codigo ?? ""),
      nome: String(produto.nome ?? ""),
      ncm: String(produto.ncm ?? ""),
      classificacao: String(produto.classificacao ?? ""),
      posicao: String(produto.posicao ?? ""),
      vendavel: Boolean(produto.vendavel),
      peso:
        produto.peso !== null &&
        produto.peso !== undefined &&
        produto.peso !== ""
          ? String(produto.peso)
          : "",
      dimensao: String(produto.dimensao ?? ""),
      quantidadeAtual:
        quantidadeAtual !== null &&
        quantidadeAtual !== undefined &&
        quantidadeAtual !== ""
          ? String(quantidadeAtual)
          : "",
    });

    setShowEditOverlay(true);
  }

  function handleCloseEditOverlay() {
    if (editLoading) return;

    setShowEditOverlay(false);
    setEditError(null);
    setEditSuccess(null);
  }

  async function handleSubmitEditProduto() {
    if (!moduloAtual?.id) {
      setEditError("Módulo de estoque não identificado para esta rota.");
      return;
    }

    if (!produtoId) {
      setEditError("Produto sem ID numérico válido para edição.");
      return;
    }

    setEditLoading(true);
    setEditError(null);
    setEditSuccess(null);

    try {
      await postEditarProduto(
        resolvedApiBase,
        moduloAtual,
        produtoId,
        editForm,
        isUsuarioDiretoria
      );

      setEditSuccess("Produto atualizado com sucesso.");

      await carregarDetalhe();

      window.setTimeout(() => {
        setShowEditOverlay(false);
        setEditSuccess(null);
      }, 700);
    } catch (e: any) {
      setEditError(e?.message || "Falha ao atualizar produto.");
    } finally {
      setEditLoading(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <style>{PRODUTO_DETAIL_SIDE_PANEL_CSS}</style>

      <div
        className="produto-detail-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <aside
          className="produto-detail-panel"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="produto-detail-header">
            <div className="produto-detail-header-text">
              <div className="produto-detail-eyebrow">
                {areaTitle} / Detalhamento do produto
              </div>

              <h2 className="produto-detail-title">
                {codigoProduto} · {nome}
              </h2>

              <p className="produto-detail-subtitle">
                Consulta de saldo, custo, dados cadastrais, movimentações e
                histórico de preço.
              </p>
            </div>

            <div className="produto-detail-header-actions">
              <button
                type="button"
                className="produto-detail-action-button"
                onClick={handleOpenEditOverlay}
                disabled={loading || !!error || !produto}
              >
                Alterar dados do Produto
              </button>

              <button
                type="button"
                className="produto-detail-close-button"
                onClick={onClose}
                aria-label="Fechar detalhamento do produto"
              >
                ×
              </button>
            </div>
          </div>

          <div className="produto-detail-content">
            {loading && (
              <div className="produto-detail-notice">
                Carregando detalhamento do produto…
              </div>
            )}

            {error && !loading && (
              <div className="produto-detail-error">
                <strong>Erro ao carregar o item</strong>
                <span>{error}</span>
                <button
                  type="button"
                  className="produto-detail-retry-button"
                  onClick={onRetry}
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {!loading && !error && payload && (
              <>
                <section className="produto-detail-stats-grid">
                  <DetailStat
                    label="Saldo atual"
                    value={formatQuantidade(quantidadeAtual)}
                    helper="Quantidade disponível no estoque"
                  />

                  <DetailStat
                    label="Custo unitário"
                    value={formatCurrencyIntl(custoAtual, moedaAtual)}
                    helper={`Moeda ${moedaAtual}`}
                  />

                  <DetailStat
                    label="Última movimentação"
                    value={
                      ultimoMovimento
                        ? formatDateTime(getMovimentoDate(ultimoMovimento))
                        : "—"
                    }
                    helper={
                      ultimoMovimento
                        ? String(ultimoMovimento.tipo || "Movimentação")
                        : "Sem histórico operacional"
                    }
                  />

                  <DetailStat
                    label="Último preço"
                    value={
                      ultimoPreco
                        ? formatCurrencyIntl(
                            ultimoPreco.custo_unitario,
                            moedaAtual
                          )
                        : "—"
                    }
                    helper={
                      ultimoPreco
                        ? formatDateTime(getMovimentoDate(ultimoPreco))
                        : "Sem histórico de preço"
                    }
                  />
                </section>

                <section className="produto-detail-section">
                  <div className="produto-detail-section-header">
                    <strong>Dados principais</strong>
                  </div>

                  <div className="produto-detail-field-grid">
                    <DetailField label="Código" value={codigoProduto} />
                    <DetailField label="NCM" value={produto?.ncm || "—"} />
                    <DetailField label="Nome" value={nome} />

                    <DetailField
                      label="Classificação"
                      value={produto?.classificacao || "—"}
                    />

                    <DetailField
                      label="Posição"
                      value={produto?.posicao || "—"}
                    />

                    <DetailField label="Estoque" value={estoqueNome} />

                    <DetailField
                      label="Dimensão"
                      value={produto?.dimensao || "—"}
                    />

                    <DetailField
                      label="Peso"
                      value={
                        produto?.peso !== null &&
                        produto?.peso !== undefined &&
                        produto?.peso !== ""
                          ? String(produto.peso)
                          : "—"
                      }
                    />
                  </div>
                </section>

                <section className="produto-detail-section">
                  <div className="produto-detail-section-header-with-tabs">
                    <div className="produto-detail-section-title-block">
                      <strong>Histórico do item</strong>
                      <span>
                        {activeTab === "mov"
                          ? "Movimentações classificadas por tipo operacional, com total de lançamentos e quantidade movimentada."
                          : "Registros de custo unitário e alterações de preço."}
                      </span>
                    </div>

                    <div className="produto-detail-tabs">
                      <button
                        type="button"
                        className={
                          activeTab === "mov"
                            ? "produto-detail-tab is-active"
                            : "produto-detail-tab"
                        }
                        onClick={() => setActiveTab("mov")}
                      >
                        Movimentações
                      </button>

                      <button
                        type="button"
                        className={
                          activeTab === "precos"
                            ? "produto-detail-tab is-active"
                            : "produto-detail-tab"
                        }
                        onClick={() => setActiveTab("precos")}
                      >
                        Preços
                      </button>
                    </div>
                  </div>

                  {activeTab === "mov" && (
                    <>
                      <div className="produto-detail-summary-grid">
                        <HistorySummaryItem
                          label="Entradas"
                          tone="entrada"
                          movimentacoes={
                            movimentosResumo.entradas.movimentacoes
                          }
                          quantidadeTotal={
                            movimentosResumo.entradas.quantidadeTotal
                          }
                        />

                        <HistorySummaryItem
                          label="Saídas"
                          tone="saida"
                          movimentacoes={movimentosResumo.saidas.movimentacoes}
                          quantidadeTotal={
                            movimentosResumo.saidas.quantidadeTotal
                          }
                        />

                        <HistorySummaryItem
                          label="Retornos"
                          tone="retorno"
                          movimentacoes={
                            movimentosResumo.retornos.movimentacoes
                          }
                          quantidadeTotal={
                            movimentosResumo.retornos.quantidadeTotal
                          }
                        />

                        <HistorySummaryItem
                          label="Ajustes"
                          tone="ajuste"
                          movimentacoes={movimentosResumo.ajustes.movimentacoes}
                          quantidadeTotal={
                            movimentosResumo.ajustes.quantidadeTotal
                          }
                        />

                        <HistorySummaryItem
                          label="Perdas"
                          tone="perda"
                          movimentacoes={movimentosResumo.perdas.movimentacoes}
                          quantidadeTotal={
                            movimentosResumo.perdas.quantidadeTotal
                          }
                        />
                      </div>
                      §
                      {movimentos.length > 0 && ultimoMovimento && (
                        <div className="produto-detail-correction-bar">
                          <div className="produto-detail-correction-bar-text">
                            <strong>Correção da última movimentação</strong>
                            <span>
                              Corrija moeda e/ou custo unitário da movimentação
                              mais recente sem apagar o registro original. A
                              correção será registrada como uma nova
                              movimentação de ajuste.
                            </span>
                          </div>

                          <button
                            type="button"
                            className="produto-detail-correction-button"
                            onClick={handleOpenCorrectionOverlay}
                            disabled={loading}
                          >
                            Corrigir
                          </button>
                        </div>
                      )}
                      {movimentos.length === 0 ? (
                        <div className="produto-detail-empty">
                          Nenhuma movimentação encontrada para este produto.
                        </div>
                      ) : (
                        <div className="produto-detail-history-list">
                          {movimentos.slice(0, 60).map((item, index) => (
                            <MovementHistoryCard
                              key={`mov-${item.id || item.item_id || index}`}
                              item={item}
                              index={index}
                              moedaAtual={moedaAtual}
                            />
                          ))}

                          {movimentos.length > 60 && (
                            <div className="produto-detail-more-items">
                              Exibindo 60 de {movimentos.length} registros.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === "precos" && (
                    <>
                      <div className="produto-detail-price-summary">
                        <div className="produto-detail-price-summary-item">
                          <span>Custo atual</span>
                          <strong>
                            {formatCurrencyIntl(custoAtual, moedaAtual)}
                          </strong>
                        </div>

                        <div className="produto-detail-price-summary-item">
                          <span>Registros de preço</span>
                          <strong>{precos.length}</strong>
                        </div>

                        <div className="produto-detail-price-summary-item">
                          <span>Última alteração</span>
                          <strong>
                            {ultimoPreco
                              ? formatDateTime(getMovimentoDate(ultimoPreco))
                              : "—"}
                          </strong>
                        </div>
                      </div>

                      {precos.length === 0 ? (
                        <div className="produto-detail-empty">
                          Nenhum histórico de preço encontrado para este
                          produto.
                        </div>
                      ) : (
                        <div className="produto-detail-history-list">
                          {precos.slice(0, 60).map((item, index) => (
                            <PriceHistoryCard
                              key={`preco-${item.id || item.item_id || index}`}
                              item={item}
                              index={index}
                              moedaAtual={moedaAtual}
                            />
                          ))}

                          {precos.length > 60 && (
                            <div className="produto-detail-more-items">
                              Exibindo 60 de {precos.length} registros.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </section>
              </>
            )}
          </div>
        </aside>
      </div>

      {showCorrectionOverlay && correctionSource && (
        <div
          className="produto-correction-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Corrigir última movimentação"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseCorrectionOverlay();
            }
          }}
        >
          <div
            className="produto-correction-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="produto-edit-modal-header">
              <div>
                <div className="produto-edit-modal-title">
                  Corrigir última movimentação
                </div>

                <div className="produto-edit-modal-subtitle">
                  {codigoProduto} — somente moeda e custo unitário desta última
                  movimentação podem ser corrigidos por esta ação.
                </div>
              </div>

              <button
                type="button"
                className="produto-edit-modal-close"
                onClick={handleCloseCorrectionOverlay}
                disabled={correctionLoading}
              >
                Fechar
              </button>
            </div>

            <div className="produto-edit-modal-body">
              <div className="produto-correction-source">
                <div className="produto-correction-source-title">
                  <span>Movimentação original</span>
                  <strong>
                    {getMovementTitle(
                      correctionSource,
                      getHistoryToneLabel(getQuantityTone(correctionSource))
                    )}
                    {getMovementCode(correctionSource)
                      ? ` · ${getMovementCode(correctionSource)}`
                      : ""}
                  </strong>
                </div>

                <div className="produto-detail-history-metric-grid">
                  <HistoryMetric
                    label="Data"
                    value={formatDateTime(getMovimentoDate(correctionSource))}
                  />

                  <HistoryMetric
                    label="Quantidade"
                    value={formatQuantidade(correctionSource.quantidade)}
                  />

                  <HistoryMetric
                    label="Custo original"
                    value={formatCurrencyIntl(
                      correctionSource.custo_unitario,
                      normalizeCurrency(correctionSource.moeda) || moedaAtual
                    )}
                  />

                  <HistoryMetric
                    label="Moeda original"
                    value={
                      normalizeCurrency(correctionSource.moeda) || moedaAtual
                    }
                  />
                </div>
              </div>

              <div className="produto-correction-grid">
                <label className="produto-correction-field">
                  <span>Moeda corrigida</span>
                  <select
                    value={correctionForm.moeda}
                    disabled={correctionLoading}
                    onChange={(event) =>
                      setCorrectionForm((prev) => ({
                        ...prev,
                        moeda: event.target.value as CurrencyCode,
                      }))
                    }
                  >
                    <option value="BRL">BRL — Real</option>
                    <option value="USD">USD — Dólar americano</option>
                    <option value="EUR">EUR — Euro</option>
                  </select>
                </label>

                <EditField
                  label="Custo unitário corrigido"
                  value={correctionForm.custoUnitario}
                  disabled={correctionLoading}
                  inputMode="decimal"
                  onChange={(value) =>
                    setCorrectionForm((prev) => ({
                      ...prev,
                      custoUnitario: value,
                    }))
                  }
                />
              </div>

              <div className="produto-correction-info">
                A movimentação original não será apagada nem reescrita. Ao
                salvar, será criada uma nova movimentação de ajuste vinculada à
                original e aplicado o novo custo/moeda ao estado atual do
                estoque.
              </div>

              {correctionError && (
                <div className="produto-edit-alert is-error">
                  {correctionError}
                </div>
              )}

              {correctionSuccess && (
                <div className="produto-edit-alert is-success">
                  {correctionSuccess}
                </div>
              )}

              <div className="produto-edit-footer">
                <button
                  type="button"
                  className="produto-edit-secondary-button"
                  onClick={handleCloseCorrectionOverlay}
                  disabled={correctionLoading}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="produto-edit-primary-button"
                  onClick={handleSubmitCorrection}
                  disabled={correctionLoading}
                >
                  {correctionLoading ? "Registrando…" : "Registrar correção"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditOverlay && (
        <div
          className="produto-edit-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Alterar dados do produto"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseEditOverlay();
            }
          }}
        >
          <div
            className="produto-edit-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="produto-edit-modal-header">
              <div>
                <div className="produto-edit-modal-title">
                  Alterar dados do produto
                </div>

                <div className="produto-edit-modal-subtitle">
                  {codigoProduto} — edita dados cadastrais
                  {isUsuarioDiretoria
                    ? ", produto vendável e permite ajuste de saldo por movimentação de ajuste."
                    : ". Quantidade, produto vendável, custo e moeda não são alterados por esta tela."}
                </div>
              </div>

              <button
                type="button"
                className="produto-edit-modal-close"
                onClick={handleCloseEditOverlay}
                disabled={editLoading}
              >
                Fechar
              </button>
            </div>

            <div className="produto-edit-modal-body">
              <div className="produto-edit-grid">
                <EditField
                  label="Código"
                  value={editForm.codigo}
                  disabled={editLoading}
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, codigo: value }))
                  }
                />

                <EditField
                  label="Nome"
                  value={editForm.nome}
                  disabled={editLoading}
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, nome: value }))
                  }
                />

                <EditField
                  label="NCM"
                  value={editForm.ncm}
                  disabled={editLoading}
                  inputMode="numeric"
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, ncm: value }))
                  }
                />

                <EditField
                  label="Classificação"
                  value={editForm.classificacao}
                  disabled={editLoading}
                  onChange={(value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      classificacao: value,
                    }))
                  }
                />

                <EditField
                  label="Posição"
                  value={editForm.posicao}
                  disabled={editLoading}
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, posicao: value }))
                  }
                />

                <EditField
                  label="Dimensão"
                  value={editForm.dimensao}
                  disabled={editLoading}
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, dimensao: value }))
                  }
                />

                <EditField
                  label="Peso"
                  value={editForm.peso}
                  disabled={editLoading}
                  inputMode="decimal"
                  onChange={(value) =>
                    setEditForm((prev) => ({ ...prev, peso: value }))
                  }
                />

                {isUsuarioDiretoria && (
                  <EditField
                    label="Quantidade atual"
                    value={editForm.quantidadeAtual}
                    disabled={editLoading}
                    inputMode="numeric"
                    type="number"
                    min="0"
                    step="1"
                    onChange={(value) =>
                      setEditForm((prev) => ({
                        ...prev,
                        quantidadeAtual: value,
                      }))
                    }
                  />
                )}
              </div>

              {isUsuarioDiretoria && (
                <div className="produto-edit-check-grid">
                  <EditCheck
                    checked={editForm.vendavel}
                    disabled={editLoading}
                    title="Produto vendável"
                    description="Permite marcar o produto como disponível para venda."
                    onChange={(checked) =>
                      setEditForm((prev) => ({
                        ...prev,
                        vendavel: checked,
                      }))
                    }
                  />
                </div>
              )}

              {editError && (
                <div className="produto-edit-alert is-error">{editError}</div>
              )}

              {editSuccess && (
                <div className="produto-edit-alert is-success">
                  {editSuccess}
                </div>
              )}

              <div className="produto-edit-footer">
                <button
                  type="button"
                  className="produto-edit-secondary-button"
                  onClick={handleCloseEditOverlay}
                  disabled={editLoading}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="produto-edit-primary-button"
                  onClick={handleSubmitEditProduto}
                  disabled={editLoading}
                >
                  {editLoading ? "Salvando…" : "Salvar alterações"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProdutoDetailSidePanel;
